import React from "react";

import {
  generateAudioWithDuolingoTts,
  getDuolingoTtsGenerationStatus,
  getDuolingoTtsLanguage,
  getDuolingoTtsVoiceNames,
  parseStatus,
} from "../../../api/duolingoTts";
import {
  getCustomVoiceEndpointStatus,
  getTtsGenerationAudio,
  getTtsGenerationVoice,
  getTtsGenerationVoiceList,
  spinUpCustomVoiceEndpoint,
} from "../../../api/ttsGenerationBackend";
import {
  getStorableDuolingoDuolingoTtsRequests,
  getStoredDuolingoTtsHistory,
} from "../../../util/duolingoTtsStorage";
import { StorageKey, storeItem } from "../../../util/storage";
import { Error } from "../../Error";
import type { InputStyle, TargetService } from "../AudioGenerator";

import { BulkEntryScreen } from "./BulkEntryScreen";
import { SingleItemEntryScreen } from "./SingleItemEntryScreen";

export const displayNamesMapping = {
  breakTime: "Break Time (ms)",
  isRawSsml: "Treat SSML as raw?",
  prosody: "Prosody Rate",
  speed: "Speed *",
  ssml: "SSML",
  text: "Text *",
  voice: "Voice *",
};

export const universalFields: (keyof typeof displayNamesMapping)[] = [
  "ssml",
  "text",
  "voice",
];
export const duolingoTtsFields: (keyof typeof displayNamesMapping)[] = [
  "speed",
];
export const duolingoTtsSpeeds = ["normal", "slow"];
export const ttsGenerationFields: (keyof typeof displayNamesMapping)[] = [
  "isRawSsml",
  "prosody",
  "breakTime",
];

export interface CommonEntryScreenProps {
  isSubmitted: boolean;
  lastUpdated: Date;
  setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  setLastUpdated: React.Dispatch<React.SetStateAction<Date>>;
  setShouldPollForStatus: React.Dispatch<React.SetStateAction<boolean>>;
  shouldPollForStatus: boolean;
  targetService: TargetService;
  voices: string[];
  voiceParam: string | null;
}

export type RowData = string | number | boolean | undefined;

export const doDuolingoTtsRequest = async (
  row: Record<string, RowData>,
  { shouldSubmitNewRequest }: { shouldSubmitNewRequest?: boolean },
) => {
  if (
    typeof row.voice !== "string" ||
    typeof row.text !== "string" ||
    typeof row.speed !== "string"
  ) {
    return "error";
  }
  const languageId = await getDuolingoTtsLanguage(row.voice);
  try {
    if (shouldSubmitNewRequest) {
      const response = await generateAudioWithDuolingoTts(
        {
          speeds: [row.speed],
          ssml_body:
            typeof row.ssml === "string" ? { [row.text]: row.ssml } : {},
          texts: [row.text],
          voices: [row.voice],
        },
        languageId,
      );
      const status = parseStatus(response);
      return status;
    } else {
      return await getDuolingoTtsGenerationStatus(
        {
          speed: row.speed,
          ssml_body: typeof row.ssml === "string" ? row.ssml : undefined,
          text: row.text,
          voice: row.voice,
        },
        languageId,
      );
    }
  } catch {
    return "error";
  }
};

export const doTtsGenerationRequest = async (
  row: Record<string, RowData>,
  { onGenerated }: { onGenerated?: (mp3Data: Blob) => void },
) => {
  if (
    typeof row.voice !== "string" ||
    typeof row.text !== "string" ||
    typeof row.status !== "string" ||
    !(row.ssml === undefined || typeof row.ssml === "string") ||
    !(row.isRawSsml === undefined || typeof row.isRawSsml === "boolean") ||
    !(row.breakTime === undefined || typeof row.breakTime === "number") ||
    !(row.prosody === undefined || typeof row.prosody === "number")
  ) {
    return "error";
  }

  const voice = await getTtsGenerationVoice(row.voice);
  if (!voice) {
    return "error";
  }

  if (voice.world_character && voice.provider === "azure") {
    const status = await getCustomVoiceEndpointStatus(voice.name);
    switch (status) {
      case "SUSPENDED":
      case "SPINNING_DOWN":
        spinUpCustomVoiceEndpoint(voice.name);
        return "creating";
      case "UNKNOWN":
        return "error";
      case "SPINNING_UP":
        return "creating";
      case "ACTIVE":
        break;
    }
  }

  try {
    const mp3Data = await getTtsGenerationAudio({
      breakTime: row.breakTime,
      featureName: "audio_generator",
      isRawSsml: row.isRawSsml,
      languageId: voice.primary_language,
      prosody: row.prosody,
      ssml: row.ssml,
      text: row.text,
      voiceName: voice.name,
      voiceVersion: voice.version,
    });
    onGenerated?.(mp3Data);
    return "created";
  } catch {
    return "error";
  }
};

export const persistDuolingoTtsRows = (rows: Record<string, RowData>[]) => {
  const currentPersistedRows = getStoredDuolingoTtsHistory();
  const newPersistableRows = getStorableDuolingoDuolingoTtsRequests(rows);
  const allRows = [...currentPersistedRows, ...newPersistableRows];
  storeItem(StorageKey.GENERATED_DUOLINGO_TTS, allRows);
};

export const EntryScreen = ({
  inputStyle,
  targetService,
  voiceParam,
}: {
  inputStyle: InputStyle | undefined;
  targetService: TargetService | undefined;
  voiceParam: string | null;
}) => {
  const [voices, setVoices] = React.useState<string[]>([]);
  const [shouldPollForStatus, setShouldPollForStatus] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date());
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const voiceNames =
        targetService === "duolingo-tts"
          ? ((await getDuolingoTtsVoiceNames()) ?? [])
          : (await getTtsGenerationVoiceList()).map(voice => voice.name);
      setVoices(voiceNames.sort());
    })();
  }, [targetService]);

  if (inputStyle === undefined || targetService === undefined) {
    return <Error subject="Whoops! Something went wrong. Please start over" />;
  }
  const commonProps = {
    isSubmitted,
    lastUpdated,
    setIsSubmitted,
    setLastUpdated,
    setShouldPollForStatus,
    shouldPollForStatus,
    targetService,
    voiceParam,
    voices,
  };

  return inputStyle === "single" ? (
    <SingleItemEntryScreen {...commonProps} />
  ) : (
    <BulkEntryScreen {...commonProps} />
  );
};
